import { h } from 'preact';

import { SCENE_CSS } from './keyframes';
import {
    layerTransform, DEPTH_SKY, DEPTH_FAR, DEPTH_MID, DEPTH_GROUND, DEPTH_HERO,
} from './parallax';
import { Cloud, Tree, GroundBand } from './sceneProps';

// A target stand downrange: post + board with scoring rings.
const TargetStand = ({ x, palette }) => (
    <g>
        <rect x={x - 3} y='92' width='6' height='34' fill={palette.mid} />
        <rect x={x - 16} y='60' width='32' height='32' rx='3' fill={palette.mid} />
        <circle cx={x} cy='76' r='10' fill='none' stroke={palette.backdrop} stroke-width='2' opacity='0.85' />
        <circle cx={x} cy='76' r='3.5' fill={palette.backdrop} opacity='0.85' />
    </g>
);

const Marksman = ({
    progress = 0, pastThreshold = false, released = false,
    pullOffset = 0, palette = {},
}) => {
    // Seconds per 800px drift loop — the world drifts by faster as you pull.
    const driftSpeed = (26 - 14 * progress).toFixed(2);
    const iconColor = released ? palette.accent : palette.text;

    return (
        <div style={`position: relative; width: 100%; height: 100%; ${pastThreshold ? 'filter: brightness(1.1);' : ''}`}>
            <svg
                viewBox='0 0 800 150'
                preserveAspectRatio='xMidYMax slice'
                role='img'
                class='refresh-scene'
                style='position: absolute; inset: 0; width: 100%; height: 100%; overflow: visible;'
            >
                <style>{SCENE_CSS}</style>
                <title>Dra ned for å oppdatere</title>

                {/* Sky */}
                <g style={layerTransform(pullOffset, DEPTH_SKY)}>
                    <Cloud x={90} y={26} fill={palette.sky} />
                    <Cloud x={330} y={16} s={0.8} fill={palette.sky} />
                    <Cloud x={540} y={30} s={1.1} fill={palette.sky} />
                    <Cloud x={730} y={20} s={0.7} fill={palette.sky} />
                </g>

                {/* Far — berm behind the range */}
                <g style={layerTransform(pullOffset, DEPTH_FAR)}>
                    <path d='M-100 150 L-100 98 Q 60 76 210 94 T 470 90 T 700 96 L 900 92 L 900 150 Z' fill={palette.far} />
                </g>

                {/* Mid — target stands + trees, drifting by */}
                <g style={layerTransform(pullOffset, DEPTH_MID)}>
                    <g class='drift' style={`animation-duration: ${driftSpeed}s;`}>
                        {[0, 800].map((off) => (
                            <g key={off} transform={`translate(${off} 0)`}>
                                <TargetStand x={70} palette={palette} />
                                <Tree x={150} fill={palette.mid} />
                                <Tree x={215} y={80} s={0.7} fill={palette.mid} />
                                <TargetStand x={300} palette={palette} />
                                <Tree x={520} y={78} s={0.8} fill={palette.mid} />
                                <TargetStand x={620} palette={palette} />
                                <Tree x={715} fill={palette.mid} />
                            </g>
                        ))}
                    </g>
                </g>

                {/* Ground — with drifting range-distance marks */}
                <g style={layerTransform(pullOffset, DEPTH_GROUND)}>
                    <GroundBand fill={palette.ground} />
                    <g class='drift' style={`animation-duration: ${(driftSpeed / 2).toFixed(2)}s;`}>
                        {[0, 800].map((off) => (
                            <g key={off} transform={`translate(${off} 0)`}>
                                {[40, 140, 240, 340, 440, 540, 640, 740].map((x) => (
                                    <line key={x} x1={x} y1='138' x2={x + 12} y2='138' stroke={palette.text} stroke-width='2' opacity='0.35' />
                                ))}
                            </g>
                        ))}
                    </g>
                </g>

                {/* Release — gold shockwave from the bullseye */}
                {released && (
                    <g>
                        <circle cx='400' cy='92' fill='none' stroke={palette.accent} stroke-width='3'>
                            <animate attributeName='r' from='10' to='90' dur='0.8s' fill='freeze' />
                            <animate attributeName='opacity' from='1' to='0' dur='0.8s' fill='freeze' />
                        </circle>
                    </g>
                )}
            </svg>

            {/* Hero — bullseye standing on the ground */}
            <div style={`position: absolute; inset: 0; display: flex; align-items: flex-end; justify-content: center; pointer-events: none; ${layerTransform(pullOffset, DEPTH_HERO)}`}>
                <i
                    class='fa-duotone fa-bullseye-arrow'
                    style={`font-size: 84px; color: ${iconColor}; --fa-secondary-opacity: 0.5; padding-bottom: 26px; ${released ? 'animation: refreshIconFlash 0.7s ease-out;' : ''}`}
                />
            </div>
        </div>
    );
};

export default Marksman;
